import axios from 'axios'
import { API_URL, getAuthHeader } from '@/services/api.service.js'
import { logger } from '@/utils/logger'

const baseUrl = async () => `${await API_URL()}/user-groups`

/* ------------------------------- Queries -------------------------------- */
/* Read paths swallow errors and return an empty list (consistent with the    */
/* other api services); mutations below throw so Vue Query surfaces failures.  */

export const getUserGroups = async ({ boardId }) => {
  try {
    const ret = await axios.get(`${await baseUrl()}/${boardId}`, {
      headers: { ...(await getAuthHeader()) }
    })
    if (ret.status === 200) return ret.data
    throw new Error(ret.statusText)
  } catch (error) {
    logger.error(error.message)
    return []
  }
}

export const getGroupMembers = async ({ boardId, groupId }) => {
  try {
    const ret = await axios.get(`${await baseUrl()}/${boardId}/${groupId}/members`, {
      headers: { ...(await getAuthHeader()) }
    })
    if (ret.status === 200) return ret.data
    throw new Error(ret.statusText)
  } catch (error) {
    logger.error(error.message)
    return []
  }
}

export const getGroupShares = async ({ boardId, groupId }) => {
  try {
    const ret = await axios.get(`${await baseUrl()}/${boardId}/${groupId}/shares`, {
      headers: { ...(await getAuthHeader()) }
    })
    if (ret.status === 200) return ret.data
    throw new Error(ret.statusText)
  } catch (error) {
    logger.error(error.message)
    return []
  }
}

/* ------------------------------- Mutations ------------------------------ */

export const createUserGroup = async ({ boardId, name }) => {
  const ret = await axios.post(
    `${await baseUrl()}/${boardId}`,
    { data: { name } },
    { headers: { ...(await getAuthHeader()) } }
  )
  if (ret.status === 201) return ret.data
  throw new Error(ret.statusText)
}

export const updateUserGroup = async ({ boardId, groupId, name }) => {
  const ret = await axios.patch(
    `${await baseUrl()}/${boardId}/${groupId}`,
    { data: { name } },
    { headers: { ...(await getAuthHeader()) } }
  )
  if (ret.status === 200) return ret.data
  throw new Error(ret.statusText)
}

export const deleteUserGroup = async ({ boardId, groupId }) => {
  const ret = await axios.delete(`${await baseUrl()}/${boardId}/${groupId}`, {
    headers: { ...(await getAuthHeader()) }
  })
  if (ret.status === 200) return ret.data
  throw new Error(ret.statusText)
}

export const addGroupMember = async ({ boardId, groupId, email }) => {
  const ret = await axios.post(
    `${await baseUrl()}/${boardId}/${groupId}/members`,
    { data: { email } },
    { headers: { ...(await getAuthHeader()) } }
  )
  if (ret.status === 201 || ret.status === 200) return ret.data
  throw new Error(ret.statusText)
}

export const removeGroupMember = async ({ boardId, groupId, memberId }) => {
  const ret = await axios.delete(`${await baseUrl()}/${boardId}/${groupId}/members/${memberId}`, {
    headers: { ...(await getAuthHeader()) }
  })
  if (ret.status === 200) return ret.data
  throw new Error(ret.statusText)
}

export const shareScenario = async ({ boardId, groupId, planBoardId }) => {
  const ret = await axios.post(
    `${await baseUrl()}/${boardId}/${groupId}/share`,
    { data: { planBoardId } },
    { headers: { ...(await getAuthHeader()) } }
  )
  if (ret.status === 201 || ret.status === 200) return ret.data
  throw new Error(ret.statusText)
}

export const unshareScenario = async ({ boardId, groupId, planBoardId }) => {
  const ret = await axios.delete(`${await baseUrl()}/${boardId}/${groupId}/share/${planBoardId}`, {
    headers: { ...(await getAuthHeader()) }
  })
  if (ret.status === 200) return ret.data
  throw new Error(ret.statusText)
}

/**
 * First-login claim: applies any pending scenario invitations for the signed-in
 * user. Auth-only, no boardId. Fire-and-forget from the FE (see user.module.js).
 */
export const claimUserGroupInvitations = async () => {
  const ret = await axios.post(
    `${await baseUrl()}/claim`,
    {},
    { headers: { ...(await getAuthHeader()) } }
  )
  if (ret.status === 200) return ret.data
  throw new Error(ret.statusText)
}
